import Axios from 'axios'
import { takeLatest, put, select, call, all } from 'redux-saga/effects'
import { saveAs } from 'file-saver'
import { api, apiAsync, downloadFile } from './helpers'
import {
  COMMON_EXPORT_CSV,
  COMMON_GET_FILE,
  COMMON_GET_WP_CONTENT_ATTEMPT,
  COMMON_SCROLL_TO_ELEMENT,
  COMMON_EMAIL_NOTIFICATION_POST,
  COMMON_EMAIL_NOTIFICATION_DISMISS,
  COMMON_EXPORT_POSTINGS_UPDATES_CSV
} from './actionTypes'
import { apiToken } from '../account/selectors'
import { apiBasePath } from '../app/selectors'
import { wpContent, getContentSuccess, getContentFailure, scrollToElement, emailNotificationSuccess, emailNotificationFail, notificationClose, alertOpen } from './actions'

export function * subscribeEmailNotification(data) {
  try {
    const token = yield select(apiToken)
    const apiBase = yield select(apiBasePath)
    const headers = {
      'Content-Type': 'application/json',
      'token': token
    }
    const apiResponse = yield call(Axios, {
      url: apiBase + '/subscribe/emailNotifications',
      method: 'POST',
      headers: headers,
      data: JSON.stringify(data.payload)
    })

    yield put(notificationClose())
    yield put(emailNotificationSuccess(apiResponse))
    yield put(alertOpen({
      type: 'success',
      message: 'You were subscribed to email notifications successfully'
    }))
  } catch ({ error }) {
    yield put(emailNotificationFail(error))
    yield put(alertOpen({
      type: 'error',
      message: 'There was an error, please try again later'
    }))
  }
}

export function * dismissEmailNotification(data) {
  yield put(notificationClose())
  try {
    const token = yield select(apiToken)
    const apiBase = yield select(apiBasePath)
    const headers = {
      'Content-Type': 'application/json',
      'token': token
    }
    const apiResponse = yield call(Axios, {
      url: apiBase + '/banner/dismiss',
      method: 'POST',
      headers: headers,
      data: JSON.stringify(data.payload)
    })
    if (!apiResponse.error) {
      yield put(alertOpen({
        type: 'success',
        message: 'Your email notifications were successfully disabled'
      }))
    }
  } catch ({ error }) {
    yield put(alertOpen({
      type: 'error',
      message: 'There was an error, please try again later'
    }))
  }
}

function * exportCSV (action) {
  const apiResponse = yield api({
    avoidGlobalLoading: true,
    api: 'export/csv',
    dataType: 'formPost',
    data: {
      data: JSON.stringify(action.payload.data),
      filename: JSON.stringify(action.payload.filename),
      headers: JSON.stringify(action.payload.headers)
    },
    method: 'POST'
  })
  window.location = apiResponse.data.link
}

function * postingsUpdatesCSVExport (action) {
  const apiResponse = yield apiAsync({
    api: `postings/updates/${action.payload.companyId}/export`,
    data: JSON.stringify(action.payload.data),
    params: action.payload.params,
    method: 'POST'
  })

  if (apiResponse && apiResponse.downloadUrl) {
    downloadFile(apiResponse.downloadUrl)
  } else {
    console.error('CSV generation error.')
  }
}

function * getAxiosWordPressContentRequest (pageId) {
  yield call(Axios, {
    url: `https://www.govdocs.com/${pageId}/?feed=json`,
    method: 'get',
    responseType: 'json'
  })
}

function * getWordPressContent () {
  const src = ['compliance-faq', 'dashboard-faq', 'minimum-wage-FAQ', 'poster-check-faq', 'whats-new']

  const sources = yield all(
    src.map(src =>
      call(Axios, {
        url: `https://www.govdocs.com/${src}/?feed=json`,
        method: 'get',
        responseType: 'json'
      })
    )
  )

  // console.log("commonSaga - getContentSuccess", sources);
  if (sources.filter(x => x.status !== 200).length === 0) {
    // console.log("commonSaga - getContentSuccess", sources);

    const returnData = {}

    src.forEach((s, idx) => {
      const data = Object.assign(sources[idx].data[0], {
        key: s
      })
      returnData[sources[idx].data[0].id] = data
    })

    yield put(getContentSuccess(returnData))
  } else {
    // console.log("commonSaga - getContentFailure");
  }
}

function * getFile (action) {
  const apiResponse = yield api({
    avoidGlobalLoading: true,
    api: `files/${action.payload.type}/${action.payload.id}`
  })

  if (!apiResponse || !apiResponse.base64) {
    return
  }

  try {
    let byteCharacters = atob(apiResponse.base64)
    let byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    let byteArray = new Uint8Array(byteNumbers)
    let blob = new Blob([byteArray], {type: 'application/octet-stream'})
    saveAs(blob, `${action.payload.name}.pdf`)
  } catch (err) {
    yield put(alertOpen({
      type: 'error',
      message: 'There was an error, please try again later'
    }))
  }
}

function * elementScroll (args) {
  if (args.payload.element) {
    const elem = args.payload.element
    const startY = window.scrollY
    const endY = elem.offsetTop
    let startTime = null
    let current = startY
    const duration = 500

    const scrollStep = timestamp => {
      if (!startTime) {
        startTime = timestamp
      }
      if (timestamp - startTime >= duration) {
        current = endY
      } else {
        current = (endY - startY) * ((timestamp - startTime) / duration) + startY
      }
      if (Math.abs(current - endY) < 1) {
        current = endY
      }
      window.scrollTo(0, current)
      if (current !== endY) {
        window.requestAnimationFrame(scrollStep)
      }
    }

    window.requestAnimationFrame(scrollStep)
  } else {
  }
}

export default function * commonSaga () {
  yield takeLatest(COMMON_GET_WP_CONTENT_ATTEMPT, getWordPressContent)

  yield takeLatest(COMMON_SCROLL_TO_ELEMENT, elementScroll)

  yield takeLatest(COMMON_GET_FILE, getFile)

  yield takeLatest(COMMON_EXPORT_CSV, exportCSV)

  yield takeLatest(COMMON_EXPORT_POSTINGS_UPDATES_CSV, postingsUpdatesCSVExport)

  yield takeLatest(COMMON_EMAIL_NOTIFICATION_POST, subscribeEmailNotification)

  yield takeLatest(COMMON_EMAIL_NOTIFICATION_DISMISS, dismissEmailNotification)

  yield put(wpContent())
}
